import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect, useRef } from "react";
import * as FaIcons from "react-icons/fa";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import "../../styles/tailwind.css";
import Button from "../base/Button";
import BackButton from "../base/BackButton";
import CustomSearch from "../base/CustomSearch";
import CustomInput from "../base/CustomInput";

function CheckListCategories() {
  const [permissions, setPermissions] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Create state
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const createInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Load permissions
  useEffect(() => {
    const pers = getPermissions();
    if (pers) setPermissions(pers);
  }, []);

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value),
      currentPage: 1,
    }));
  };

  const handlePageChange = (selectedItem) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selectedItem.selected + 1,
    }));
  };

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        perPage: pagination.perPage,
      };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const res = await apiUrl.get("/getChecklistCategory", { params });
      if (res.data?.status) {
        setCategories(res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.pagination.total,
          lastPage: res.data.pagination.lastPage,
          currentPage: res.data.pagination.currentPage,
        }));
      } else throw new Error(res.data?.message || "Failed to load");
    } catch (err) {
      showToast("Failed to load categories.", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchCategories();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [pagination.currentPage, pagination.perPage, searchTerm]);

  // Auto-focus create input
  useEffect(() => {
    if (showCreateInput && createInputRef.current) {
      createInputRef.current.focus();
    }
  }, [showCreateInput]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(
      () => setToast({ show: false, message: "", type: "success" }),
      3000
    );
  };

  // Create handler
  const handleCreate = async () => {
    const name = newCategoryName.trim();
    if (!name) {
      showToast("Category name is required.", "warning");
      return;
    }

    try {
      setSaving(true);
      const res = await apiUrl.post("/createChecklistCategory", { name });
      if (res.data?.status) {
        fetchCategories();
        setNewCategoryName("");
        setShowCreateInput(false);
        Swal.fire({
          title: "CheckList Created Successfully",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
      } else throw new Error(res.data?.message || "Creation failed");
    } catch (err) {
      Swal.fire({
        title: err.response?.data?.error || "Error",
        icon: "error",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEdit = (category) => {
    setEditingId(category.id);
    setEditValue(category.name);
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editValue.trim()) {
      showToast("Category name cannot be empty.", "warning");
      return;
    }

    const current = categories.find((c) => c.id === editingId);
    if (!current || current.name === editValue) {
      setEditingId(null);
      return;
    }

    try {
      setSaving(true);
      const res = await apiUrl.put(`/updateChecklistCategory/${editingId}`, {
        name: editValue,
      });
      if (res.data?.status) {
        Swal.fire({
          title: "CheckList Updated Successfully",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        fetchCategories();
        setEditingId(null);
      } else throw new Error(res.data?.message || "Update failed");
    } catch (err) {
      Swal.fire({
        title: err.response?.data?.error || "Error",
        icon: "error",
        timer: 3000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
  };

  // Delete
  const handleDelete = async (id, name) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await apiUrl.delete(
            `/deleteChecklistCategory/${id}`
          );
          if (response?.data?.status) {
            Swal.fire({
              title: response.data.message,
              icon: "success",
              timer: 3000,
              toast: true,
              showConfirmButton: false,
              position: "top-end",
            });
            fetchCategories();
            setSaving(false);
          }
        } catch (error) {
          Swal.fire({
            title: error.response?.data?.message || "Error deleting department",
            icon: "error",
            timer: 3000,
            toast: true,
            showConfirmButton: false,
            position: "top-end",
          });
        }
      }
    });
  };

  // Enter key handler
  const handleKeyDown = (e, action) => {
    if (e.key === "Enter") {
      e.preventDefault();
      action();
    }
  };

  if (!permissions?.manage_checklist) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning">
          You don't have permission to manage checklist categories.
        </div>
      </div>
    );
  }

  return (
    <>
      {permissions?.manage_checklist && (
        <div className="card px-4 py-3 bg-white">
          {/* Header */}
          <header className="mb-3">
            <div className="flex align-center justify-between flex-wrap">
              <div className="flex align-center gap-3">
                <BackButton url="/checklist" />
              </div>

              {/* ✅ Add Category Button — outside everything */}
              {!showCreateInput && (
                <div>
                  <Button
                    text="Add Category"
                    size="md"
                    variant="primary"
                    onClick={() => {
                      setShowCreateInput(true);
                    }}
                    disabled={saving}
                  />
                </div>
              )}
            </div>
          </header>

          <div className="flex items-center justify-between mb-2">
            <h1 className="h4 font-semibold">Checklist Categories</h1>
            <CustomSearch
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              onClear={() => {
                setSearchTerm("");
              }}
            />
          </div>

          {showCreateInput && (
            <div className="w-full flex sm:flex-row items-center gap-2  mb-2">
              {/* Input */}
              <div>
                <CustomInput
                  ref={createInputRef}
                  label="New Category : "
                  placeholder="Enter category name"
                  value={newCategoryName}
                  onChange={(e) => {
                    setNewCategoryName(e.target.value);
                  }}
                />
              </div>
              {/* Buttons */}
              <div className="flex gap-2 ">
                <Button
                  text="Save"
                  size="md"
                  variant="success"
                  onClick={() => {
                    handleCreate();
                  }}
                  isDisabled={saving || !newCategoryName.trim()}
                  isLoading={saving}
                />
                <Button
                  text="Cancel"
                  size="md"
                  variant="secondary"
                  onClick={() => {
                    setShowCreateInput(false);
                    setNewCategoryName("");
                  }}
                />
              </div>
            </div>
          )}

          {/* Table */}
          <table className="table w-full">
            <thead>
              <tr>
                <th>Category Name</th>
                <th>Attached CheckLists</th>
                <th>Created/updated By</th>
                <th>Created At</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {/* ---------- SKELETON (DaisyUI style) ---------- */}
              {loading ? (
                <>
                  {[...Array(3)].map((_, i) => (
                    <tr key={i}>
                      {/* Category Name */}
                      <td className="py-3">
                        <div className="h-5 w-32 bg-gray-300 rounded animate-pulse"></div>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 bg-gray-300 rounded-full animate-pulse"></div>
                        </div>
                      </td>
                      {/* Created By */}
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 bg-gray-300 rounded-full animate-pulse"></div>
                          <div className="h-4 w-20 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                      </td>
                      {/* Created At */}
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 bg-gray-300 rounded animate-pulse"></div>
                          <div className="h-4 w-24 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                      </td>
                      {/* Actions */}
                      <td className="py-3 text-right pr-6">
                        <div className="flex justify-end gap-2">
                          <div className="h-8 w-16 bg-gray-300 rounded animate-pulse"></div>
                          <div className="h-8 w-16 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : categories.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-6 text-base-content/60"
                  >
                    No checklist categories found.
                  </td>
                </tr>
              ) : (
                /* ---------- REAL ROWS (your original logic) ---------- */
                categories.map((category) => (
                  <tr key={category.id}>
                    <td className=" align-middle">
                      {editingId === category.id ? (
                        <div className="col-6">
                          <CustomInput
                            ref={createInputRef}
                            placeholder="Edit category name"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                          />
                          {!editValue.trim() && (
                            <span className="text-error text-sm">Required</span>
                          )}
                        </div>
                      ) : (
                        <span className="font-medium">{category.name}</span>
                      )}
                    </td>
                    <td className="align-middle">
                      <span
                        className="
                          inline-flex items-center justify-center
                          px-3 py-1
                          text-sm font-semibold
                          rounded-full
                          bg-blue-100 text-blue-700
                          border border-blue-200
                          shadow-sm
                        "
                      >
                        {category?.checklists_count ?? "—"}
                      </span>
                    </td>

                    <td className=" align-middle">
                      <span className="flex items-center gap-2 text-sm text-base-content/70">
                        <FaIcons.FaUser size={12} />
                        {category?.user?.name || "—"}
                      </span>
                    </td>

                    <td className=" align-middle">
                      <span className="flex items-center gap-2 text-sm text-base-content/70">
                        <FaIcons.FaClock size={12} />
                        {new Date(category?.created_at).toLocaleDateString()}
                      </span>
                    </td>

                    <td className=" mb-1 align-middle">
                      {editingId === category.id ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            text="Save"
                            variant="success"
                            isDisabled={saving || !editValue.trim()}
                            onClick={handleSaveEdit}
                          ></Button>
                          <Button
                            text="Cancel"
                            variant="secondary"
                            isDisabled={saving}
                            onClick={cancelEdit}
                          ></Button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            text="Edit"
                            variant="primary"
                            size="sm"
                            onClick={() => startEdit(category)}
                            isDisabled={saving}
                          ></Button>
                          <Button
                            text="Delete"
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(category.id)}
                            isDisabled={saving}
                          ></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Toast Notifications */}
          <ToastContainer position="top-end" className="p-3">
            <Toast
              bg={toast.type}
              show={toast.show}
              onClose={() => setToast({ ...toast, show: false })}
              delay={3000}
              autohide
            >
              <Toast.Body className="text-white">{toast.message}</Toast.Body>
            </Toast>
          </ToastContainer>

          <div className="row align-items-center mb-5 mt-2">
            <div className="col-auto">
              <select
                value={pagination?.perPage}
                onChange={handlePerPageChange}
                className="form-select"
                style={{ minWidth: 80 }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <div className="col-auto text-muted">
              Page {pagination.currentPage} – Showing{" "}
              {Math.min(
                pagination.perPage,
                pagination.total -
                  (pagination.currentPage - 1) * pagination.perPage
              )}{" "}
              of {pagination.total} entries
            </div>
            <div className="col">
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                breakLabel={"..."}
                pageCount={pagination.lastPage}
                marginPagesDisplayed={2}
                pageRangeDisplayed={3}
                onPageChange={handlePageChange}
                forcePage={pagination.currentPage - 1}
                containerClassName={"pagination justify-content-end mb-0"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
                disabledClassName={"disabled"}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CheckListCategories;
